import { isEmpty } from "lodash";
import { createSelector } from "reselect";

import { ScanNlisStatus } from "constants/nlis";
import type { ParentSaleTabType } from "constants/sale";
import { Settings } from "constants/settings";

import { EMPTY_ARRAY } from "lib";

import { buildBusinessUserDataListBasedOnParentTab } from "lib/parentTabList";

import { selectPenScanLotAggridDataByIdLookup } from "./penScanLots";
import { selectReceivalLotAggridDataByIdLookup } from "./receivalLots";
import { selectAgencyByDeploymentIdLookup } from "../agencies";
import { selectBuyerBySaleLotIdLookup } from "../businesses";
import {
  selectResolvedSaleLotCommentCountBySaleLotIdLookup,
  selectUnresolvedSaleLotCommentCountBySaleLotIdLookup,
} from "../comments";
import { selectAgencyBySaleLotIdLookup } from "../consignmentsCalculated";
import { getCurrentSale } from "../currentSale";
import { selectFilteredEids } from "../globalSearch/scanFilters";
import { selectVendorIdBySaleLotIdLookup } from "../indexes";
import {
  selectIsExportP2PInTransferrableByEidLookup,
  selectIsExportP2POutTransferrableByEidLookup,
  selectIsP2PTransferrableByEidLookup,
  selectIsSellableByEid,
  selectIsTakeableByEid,
} from "../nlis";
import { selectIsRollbackAvailableByTypeById } from "../nlisFiles";
import { selectConsignmentByReceivalLotIdLookup } from "../receivalLots";
import { selectScanLabelsByScanIdLookup } from "../scanLabel";
import {
  getActiveNLISSaleyardCredentials,
  getActiveRole,
  getBusinesses,
  getConsignments,
  getCurrentUser,
  getDraftingInformation,
  getLabels,
  getProperties,
  getSaleyardScanSaleLots,
  getScans,
  getSellFiles,
  getSettings,
  getTakeFiles,
  getWeighLots,
  getWeighLotScans,
} from "../root";
import { getSaleLotsBySale } from "../saleLots/index";

// `getSaleLotsBySale` returns sale lots already joined with their
// consignment and vendor name (a denormalisation that happens upstream of
// this module, which the plain `SaleLot` state shape doesn't carry).
// `getSaleyardScanSaleLots` doesn't do this join, so both are resolved
// separately for that variant - always wrap this type in `Partial<>` where
// a saleyard-scan sale lot is possible (see `scanList` below).
interface EnrichedSaleLot extends SaleLot {
  consignment: Consignment;
  vendor_name: string;
}

// The leaf shape produced by `getTransferByTransactionAndEidLookup`: an eid
// entry merged with its transfer record (`Object.assign({}, eid, transfer)`),
// so it's the intersection of both - see `NlisTransferEid`/`NlisTransfer`
// (state.d.ts) for where each half comes from.
type ScanTransfer = NlisTransferEid & NlisTransfer;

// Shared subset of `ScanListRow` used by every NLIS-file/transfer grid row.
// Consumers extend this with whatever `saleLot` fields they display, rather
// than independently repeating the same `Pick`.
export type ScanTransferRowBase = Pick<
  ScanListRow,
  "consignment" | "nlisSaleyardCredentials" | "scan"
>;

// The full row shape produced by the `scanList` selector below. Row-shape
// interfaces for individual consumers should be `Pick`s/intersections of
// this (or extend `ScanTransferRowBase`) rather than independently
// redeclaring it.
export interface ScanListRow {
  // TODO: `selectBuyerBySaleLotIdLookup` (../businesses) is untyped JS with
  // no obvious existing type to reuse - type this once that selector is.
  buyer: unknown;
  consignment: Consignment;
  currentUser: User;
  draftingAttributes: DraftingAttributes | null;
  isSellable: boolean;
  isTakeable: boolean;
  isTransferred: "Yes" | "No";
  latestSellFileTransactionId: string;
  latestSellTransfer: ScanTransfer | undefined;
  latestTakeFileTransactionId: string;
  latestTakeTransfer: ScanTransfer | undefined;
  // TODO: `selectAgencyBySaleLotIdLookup`/`selectAgencyByDeploymentIdLookup`
  // are untyped JS with no existing `Agency`-shaped selector return type.
  livestockAgency: unknown;
  livestockSale: Sale;
  nlisSaleyardCredentials: NLISCredentials;
  // TODO: `selectPenScanLotAggridDataByIdLookup` (./penScanLots) is untyped JS.
  penScanLot: unknown;
  // TODO: `selectReceivalLotAggridDataByIdLookup` (./receivalLots) is untyped JS.
  receivalLot: unknown;
  resolvedSaleLotCommentCount: number | undefined;
  rowWarning: string | null;
  saleLot: EnrichedSaleLot;
  scan: Scan;
  scanDraftingInformation: DraftingInformationRecord | undefined;
  scanLabels: ScanLabel[];
  unresolvedSaleLotCommentCount: number | undefined;
  // TODO: `getWeighLots`/`getWeighLotScans` (../root) back onto
  // `TransitionalState.weighLots`/`weighLotScans`, both still `any` there.
  weighLot: unknown;
  weighLotScan: unknown;
}

const getTransferByTransactionAndEidLookup = (
  nlisFiles: Record<string, NlisFileRecord>,
): Record<string, Record<string, ScanTransfer>> =>
  Object.entries(nlisFiles).reduce(
    (acc, [nlisFileId, nlisFile]) => {
      acc[nlisFileId] = nlisFile.transfers.reduce(
        (eidAcc, transfer) => {
          for (const eid of transfer.eids) {
            eidAcc[eid.EID] = Object.assign({}, eid, transfer);
          }
          return eidAcc;
        },
        {} as Record<string, ScanTransfer>,
      );
      return acc;
    },
    {} as Record<string, Record<string, ScanTransfer>>,
  );

const selectCurrentTakeTransferByTransactionAndEidLookup = createSelector(
  [getTakeFiles],
  getTransferByTransactionAndEidLookup,
);

const selectCurrentSellTransferByTransactionAndEidLookup = createSelector(
  [getSellFiles],
  getTransferByTransactionAndEidLookup,
);

export const scanList = createSelector(
  [
    getScans,
    getSaleLotsBySale,
    getSaleyardScanSaleLots,
    getConsignments,
    getDraftingInformation,
    selectUnresolvedSaleLotCommentCountBySaleLotIdLookup,
    selectResolvedSaleLotCommentCountBySaleLotIdLookup,
    selectAgencyBySaleLotIdLookup,
    selectFilteredEids,
    getTakeFiles,
    getSellFiles,
    selectBuyerBySaleLotIdLookup,
    selectCurrentTakeTransferByTransactionAndEidLookup,
    selectCurrentSellTransferByTransactionAndEidLookup,
    selectIsSellableByEid,
    selectIsTakeableByEid,
    getActiveNLISSaleyardCredentials,
    getCurrentSale,
    getCurrentUser,
    selectAgencyByDeploymentIdLookup,
    selectReceivalLotAggridDataByIdLookup,
    selectPenScanLotAggridDataByIdLookup,
    getWeighLotScans,
    selectConsignmentByReceivalLotIdLookup,
    getProperties,
    getBusinesses,
    getWeighLots,
    getLabels,
    selectScanLabelsByScanIdLookup,
  ],
  (
    scans: Record<string, Scan>,
    saleLots: EnrichedSaleLot[],
    saleyardScansSaleLots: Record<
      string,
      Partial<EnrichedSaleLot> & { consignmentId?: string }
    >,
    consignments: Record<string, Consignment>,
    draftingInformation: Record<string, DraftingInformationRecord>,
    unresolvedSaleLotCommentCountBySaleLotIdLookup: Record<string, number>,
    resolvedSaleLotCommentCountBySaleLotIdLookup: Record<string, number>,
    // TODO: see the `livestockAgency` TODO on `ScanListRow` above.
    agencyBySaleLotIdLookup: Record<string, unknown>,
    filteredEids: string[],
    takeFiles: Record<string, NlisFileRecord>,
    sellFiles: Record<string, NlisFileRecord>,
    // TODO: see the `buyer` TODO on `ScanListRow` above.
    buyerBySaleLotIdLookup: Record<string, unknown>,
    currentTakeTransferByTransactionAndEidLookup: Record<
      string,
      Record<string, ScanTransfer>
    >,
    currentSellTransferByTransactionAndEidLookup: Record<
      string,
      Record<string, ScanTransfer>
    >,
    isSellableByEid: Record<string, boolean>,
    isTakeableByEid: Record<string, boolean>,
    nlisSaleyardCredentials: NLISCredentials,
    livestockSale: Sale,
    currentUser: User,
    // TODO: see the `livestockAgency` TODO on `ScanListRow` above.
    agencyByDeploymentIdLookup: Record<string, unknown>,
    // TODO: see the `receivalLot` TODO on `ScanListRow` above.
    receivalLotAggridDataByIdLookup: Record<string, unknown>,
    // TODO: see the `penScanLot` TODO on `ScanListRow` above.
    penScanLotAggridDataByIdLookup: Record<string, unknown>,
    // TODO: see the `weighLot`/`weighLotScan` TODO on `ScanListRow` above.
    weighLotScanByIdLookup: Record<
      string,
      { weighLotId?: string } & Record<string, unknown>
    >,
    consignmentByReceivalLotIdLookup: Record<string, Consignment>,
    properties: Record<string, Property>,
    businesses: Record<string, Business>,
    weighLotByIdLookup: Record<string, unknown>,
    labelsLookup: Record<string, Label>,
    scanLabelsByScanIdLookup: Record<string, ScanLabel[]>,
  ): ScanListRow[] => {
    const saleLotsById: Record<string, EnrichedSaleLot> = {};
    for (const saleLot of saleLots) {
      saleLotsById[saleLot.id] = saleLot;
    }

    const result: ScanListRow[] = [];

    for (const eid of filteredEids) {
      const scan = scans[eid];
      const receivalLotId = scan.receival_lot_id;
      let saleLot: Partial<EnrichedSaleLot> & { consignmentId?: string } =
        saleLotsById[scan.sale_lot_id] || {};
      let consignment: Partial<Consignment> = {};
      let livestockAgency: unknown = {};
      if (!isEmpty(saleLot)) {
        // eslint-disable-next-line prefer-destructuring
        consignment = saleLot.consignment;
      } else {
        saleLot = saleyardScansSaleLots[scan.sale_lot_id] || {};
        if (!isEmpty(saleLot)) {
          consignment = consignments[saleLot.consignmentId] || {};
        } else if (receivalLotId) {
          consignment = consignmentByReceivalLotIdLookup[receivalLotId] || {};
        }
      }

      const scanDraftingInformation = draftingInformation[scan.drafting_id];

      livestockAgency = scan.deployment_id
        ? agencyByDeploymentIdLookup[scan.deployment_id]
        : agencyBySaleLotIdLookup[scan.sale_lot_id];

      const latestTakeFileTransactionId =
        takeFiles?.[scan.latest_take_file_id]?.transactionId || "";

      const latestSellFileTransactionId =
        sellFiles?.[scan.latest_sell_file_id]?.transactionId || "";

      const latestTakeTransfer =
        currentTakeTransferByTransactionAndEidLookup?.[
          scan.latest_take_file_id
        ]?.[scan.EID];

      const latestSellTransfer =
        currentSellTransferByTransactionAndEidLookup?.[
          scan.latest_sell_file_id
        ]?.[scan.EID];

      const isTransferred: "Yes" | "No" =
        scan.nlis_sell_status === ScanNlisStatus.GOOD &&
        scan.nlis_take_status === ScanNlisStatus.GOOD
          ? "Yes"
          : "No";

      const isSellable = isSellableByEid[scan.EID];
      const isTakeable = isTakeableByEid[scan.EID];

      const resolvedSaleLotCommentCount = scan.sale_lot_id
        ? resolvedSaleLotCommentCountBySaleLotIdLookup[scan.sale_lot_id]
        : undefined;
      const unresolvedSaleLotCommentCount = scan.sale_lot_id
        ? unresolvedSaleLotCommentCountBySaleLotIdLookup[scan.sale_lot_id]
        : undefined;

      const buyer = scan.sale_lot_id
        ? buyerBySaleLotIdLookup[scan.sale_lot_id]
        : undefined;

      const penScanLotId = scan.pen_scan_lot_id;

      const weighLotScanId = scan.weigh_lot_scan_id;

      const devicePic = scan.current_pic;
      const vendorProperty = properties[consignment.vendor_property_id];
      const vendorPic = vendorProperty?.PIC;

      const vendorPicExistsInAnotherConsignment = !!Object.values(
        consignments,
      ).filter(
        otherConsignment =>
          properties[otherConsignment.vendor_property_id]?.PIC === devicePic,
      ).length;

      let rowWarning = null;

      if (devicePic && vendorPic) {
        if (devicePic !== vendorPic) {
          if (vendorPicExistsInAnotherConsignment) {
            rowWarning =
              "Vendor PIC does not equal device PIC and another Consignment has this Device PIC as a Vendor PIC";
          } else {
            rowWarning =
              "Vendor PIC does not equal Device PIC and no Vendor has this Device PIC";
          }
        }
      }

      const weighLotScan = weighLotScanByIdLookup[weighLotScanId] || {};

      const weighLot = weighLotByIdLookup[weighLotScan.weighLotId];

      const rowData: ScanListRow = {
        buyer,
        consignment: Object.assign({}, consignment, {
          vendor_property: properties[consignment.vendor_property_id] || {},
        }) as Consignment,
        currentUser,
        draftingAttributes: saleLot.draftingAttributes,
        saleLot: Object.assign({}, saleLot, {
          vendor_name:
            saleLot?.vendor_name ||
            (businesses[consignment.vendor_id] || {}).name ||
            "",
          labels: saleLot.labels?.map(id => labelsLookup[id]?.name || "") || [],
        }) as EnrichedSaleLot,
        resolvedSaleLotCommentCount,
        unresolvedSaleLotCommentCount,
        isSellable,
        isTakeable,
        isTransferred,
        livestockSale,
        latestSellTransfer,
        latestTakeTransfer,
        latestSellFileTransactionId,
        latestTakeFileTransactionId,
        livestockAgency,
        nlisSaleyardCredentials,
        scan,
        scanDraftingInformation,
        scanLabels: scanLabelsByScanIdLookup[scan.id] || EMPTY_ARRAY,
        receivalLot: receivalLotAggridDataByIdLookup[receivalLotId] || {},
        penScanLot: penScanLotAggridDataByIdLookup[penScanLotId] || {},
        weighLotScan,
        weighLot,
        rowWarning,
      };
      result.push(rowData);
    }

    return result;
  },
);

export const scansListForBusinessUser = createSelector(
  [scanList, selectVendorIdBySaleLotIdLookup, getSettings, getActiveRole],
  (
    scans: ScanListRow[],
    vendorIdBySaleLotIdLookup: Record<string, string>,
    // The `Settings` state is a large heterogeneous bag keyed by
    // `constants/settings`'s `Settings.*` values, each with its own value
    // type - not worth modelling in full here.
    // TODO: type the wider `Settings` state (state.d.ts) properly one day.
    settingsLookup: Record<string, unknown>,
    // Only `BusinessUserRole` carries `.business`; the other members of the
    // `ActiveRole` union (and the placeholder) don't.
    activeRole: ActiveRolePlaceholder | ActiveRole,
  ) => {
    const { business } = activeRole as BusinessUserRole;
    const businessId = business?.id;
    const parentTab = settingsLookup[
      Settings.parentSaleTab
    ] as ParentSaleTabType;
    const scansByParentTabLookup = Object.values(scans).reduce(
      (acc, scan) => {
        const { saleLot } = scan;
        return buildBusinessUserDataListBasedOnParentTab(
          acc,
          saleLot,
          vendorIdBySaleLotIdLookup,
          businessId,
          scan,
        );
      },
      {} as Record<string, ScanListRow[]>,
    );
    return scansByParentTabLookup[parentTab];
  },
);

interface ScansByNlisTypeAndStatus {
  take: Record<string, ScanListRow[]>;
  sell: Record<string, ScanListRow[]>;
}

export const selectScansByNlisTypeAndStatus = createSelector(
  [scanList],
  (scanRows: ScanListRow[]): ScansByNlisTypeAndStatus => {
    const take: Record<string, ScanListRow[]> = {};
    const sell: Record<string, ScanListRow[]> = {};

    for (const scanRow of scanRows) {
      const { scan } = scanRow;

      if (take[scan.nlis_take_status]) {
        take[scan.nlis_take_status].push(scanRow);
      } else {
        take[scan.nlis_take_status] = [scanRow];
      }

      if (sell[scan.nlis_sell_status]) {
        sell[scan.nlis_sell_status].push(scanRow);
      } else {
        sell[scan.nlis_sell_status] = [scanRow];
      }
    }

    return { take, sell };
  },
);

export const getTakeCorrectableScans = (state: RootState): ScanListRow[] =>
  selectScansByNlisTypeAndStatus(state).take[ScanNlisStatus.NEEDS_CORRECTING] ||
  EMPTY_ARRAY;

export const getTakeCorrectableRollbackableScans = createSelector(
  [getTakeCorrectableScans, selectIsRollbackAvailableByTypeById],
  (
    scans: ScanListRow[],
    rollbackAvailableByTypeById: { NLISTakeFile: Record<string, boolean> },
  ) =>
    scans.filter(
      scan =>
        rollbackAvailableByTypeById?.NLISTakeFile[
          scan.scan.latest_take_file_id
        ],
    ),
);

export const getSellCorrectableScans = (state: RootState): ScanListRow[] =>
  selectScansByNlisTypeAndStatus(state).sell[ScanNlisStatus.NEEDS_CORRECTING] ||
  EMPTY_ARRAY;

export const getTakeableScans = createSelector(
  [scanList],
  (scans: ScanListRow[]) => scans.filter(scan => scan.isTakeable),
);

export const getSellableScans = createSelector(
  [scanList],
  (scans: ScanListRow[]) => scans.filter(scan => scan.isSellable),
);

const buildScansForTransferSelector = eligibilityLookupSelector =>
  createSelector(
    [scanList, eligibilityLookupSelector],
    (
      scans: ScanListRow[],
      isTransferrableByEidLookup: Record<string, boolean>,
    ) => scans.filter(scan => isTransferrableByEidLookup[scan.scan.EID]),
  );

export const getScansForP2PTransfer = buildScansForTransferSelector(
  selectIsP2PTransferrableByEidLookup,
);

export const getScansForExportP2PIn = buildScansForTransferSelector(
  selectIsExportP2PInTransferrableByEidLookup,
);

export const getScansForExportP2POut = buildScansForTransferSelector(
  selectIsExportP2POutTransferrableByEidLookup,
);
